import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authApi } from "./authApi";

/**
 * Auth states:
 *   user === undefined  → still checking session
 *   user === null       → anonymous
 *   user object         → logged in
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      try {
        localStorage.setItem("cm_user_profile_v1", JSON.stringify({
          id: me.id,
          name: me.name,
          email: me.email,
          avatar: me.avatar,
          createdAt: me.created_at,
          churchMember: me.church_member,
        }));
      } catch {}
      return me;
    } catch {
      setUser(null);
      try { localStorage.removeItem("cm_user_profile_v1"); } catch {}
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth, skip the /me probe.
    // AuthCallback will call /api/auth/google/exchange first to set our cookies.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    refresh();
  }, [refresh]);

  const role = (user && typeof user === "object" ? user.role : null) || null;
  const hasRole = useCallback(
    (target) => !!role && role === String(target || "").toLowerCase(),
    [role],
  );
  const hasAnyRole = useCallback(
    (...targets) => {
      if (!role) return false;
      const flat = targets.flat().map((t) => String(t || "").toLowerCase());
      return flat.includes(role);
    },
    [role],
  );
  // Hierarquia: admin > pastor > lider > premium > user
  // hasMinRole("pastor") retorna true para admin OU pastor.
  const ROLE_ORDER = ["user", "premium", "lider", "pastor", "admin"];
  const hasMinRole = useCallback(
    (minRole) => {
      if (!role) return false;
      const need = ROLE_ORDER.indexOf(String(minRole || "").toLowerCase());
      const have = ROLE_ORDER.indexOf(role);
      return need >= 0 && have >= need;
    },
    [role],
  );

  const value = {
    user,
    role,
    hasRole,
    hasAnyRole,
    hasMinRole,
    isAdmin: hasRole("admin"),
    isAuthenticated: !!user,
    isChecking: user === undefined,
    login: async (creds) => {
      const me = await authApi.login(creds);
      setUser(me);
      try {
        localStorage.setItem("cm_user_profile_v1", JSON.stringify({
          id: me.id, name: me.name, email: me.email, avatar: me.avatar,
          createdAt: me.created_at, churchMember: me.church_member,
        }));
      } catch {}
      return me;
    },
    register: async (creds) => {
      const me = await authApi.register(creds);
      setUser(me);
      return me;
    },
    updateProfile: async (payload) => {
      const me = await authApi.updateProfile(payload);
      setUser(me);
      return me;
    },
    logout: async () => {
      try { await authApi.logout(); } catch {}
      setUser(null);
      try { localStorage.removeItem("cm_user_profile_v1"); } catch {}
    },
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
